import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ShieldSlash, Copy, Check, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function TwoFactorSection() {
  const { user, refresh } = useAuth();
  const enabled = !!user?.two_factor_enabled;
  const [remaining, setRemaining] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | setup | confirm | backup | disable
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    api.get("/2fa/backup-codes/remaining").then((r) => setRemaining(r.data.remaining)).catch(() => {});
  }, [enabled]);

  const startEnroll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/2fa/setup");
      setQr(data.qr_data_url);
      setSecret(data.secret);
      setPhase("confirm");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start setup");
    } finally {
      setLoading(false);
    }
  };

  const confirmEnroll = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/2fa/enable", { code });
      setBackupCodes(data.backup_codes);
      setPhase("backup");
      setCode("");
      await refresh?.();
      toast.success("Two-factor is on.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That code didn’t match.");
    } finally {
      setLoading(false);
    }
  };

  const doDisable = async () => {
    setLoading(true);
    try {
      await api.post("/2fa/disable", { password: pw, code });
      setPhase("idle");
      setCode("");
      setPw("");
      await refresh?.();
      toast.success("Two-factor is off.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not turn off");
    } finally {
      setLoading(false);
    }
  };

  const copyBackup = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Backup codes copied.");
  };

  const downloadBackup = () => {
    const blob = new Blob(
      [
        `Clarity Mirror: Two-Factor Backup Codes\nEmail: ${user.email}\nGenerated: ${new Date().toISOString()}\n\nEach code works ONCE. Keep them somewhere safe (password manager, paper).\n\n${backupCodes.join("\n")}\n`,
      ],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clarity-mirror-backup-codes.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="rounded-3xl bg-canvas-warm border border-line p-6 sm:p-8 mb-6" data-testid="two-factor-section">
      <div className="flex items-start gap-3 mb-4">
        {enabled ? (
          <ShieldCheck size={22} weight="thin" className="text-sage shrink-0 mt-1" />
        ) : (
          <ShieldSlash size={22} weight="thin" className="text-ink-mute shrink-0 mt-1" />
        )}
        <div className="flex-1">
          <h2 className="font-serif-display text-2xl text-ink">Two-factor authentication</h2>
          <p className="text-sm text-ink-soft mt-1 leading-relaxed">
            {enabled
              ? "Extra protection is on. Even someone with your password can’t sign in without your authenticator."
              : "Add a 6-digit code from an authenticator app (Google Authenticator, 1Password, Authy) at every sign-in. Highly recommended."}
          </p>
        </div>
      </div>

      {!enabled && phase === "idle" && (
        <button
          onClick={startEnroll}
          disabled={loading}
          data-testid="enable-2fa-btn"
          className="rounded-full bg-ink text-canvas px-6 py-2.5 text-sm font-medium hover:bg-ink/90 transition-colors disabled:opacity-60"
        >
          {loading ? "Opening…" : "Turn on two-factor"}
        </button>
      )}

      <AnimatePresence>
        {phase === "confirm" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-5 mt-2" data-testid="totp-confirm">
            <p className="text-sm text-ink-soft">
              Open your authenticator app, tap the plus button, and scan this QR, or paste the secret below.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              {qr && <img src={qr} alt="2FA QR code" className="w-40 h-40 rounded-lg border border-line bg-canvas p-2" />}
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-ink-mute mb-1">Manual key</p>
                  <code className="text-xs font-mono bg-canvas border border-line rounded px-2 py-1 select-all break-all block" data-testid="totp-secret">{secret}</code>
                </div>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.2em] text-ink-mute">Enter the 6-digit code</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={7}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-testid="totp-code-input"
                    className="mt-2 w-full sm:w-48 bg-canvas border border-line rounded-lg px-3 py-2 text-lg font-mono tracking-widest text-center focus:border-clay focus:outline-none"
                    placeholder="000000"
                    autoFocus
                  />
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={confirmEnroll}
                    disabled={loading || code.length < 6}
                    data-testid="confirm-2fa-btn"
                    className="rounded-full bg-clay text-canvas px-6 py-2.5 text-sm font-medium hover:bg-clay-dark transition-colors disabled:opacity-40"
                  >
                    {loading ? "Verifying…" : "Confirm"}
                  </button>
                  <button
                    onClick={() => { setPhase("idle"); setCode(""); setQr(null); setSecret(""); }}
                    className="text-sm text-ink-soft hover:text-ink underline underline-offset-4 decoration-line-strong"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {phase === "backup" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-2" data-testid="backup-codes-display">
            <p className="text-sm text-ink font-medium mb-2">Save these backup codes somewhere safe.</p>
            <p className="text-xs text-ink-soft mb-4 leading-relaxed">
              Each code works ONCE and can replace your authenticator if you lose your phone. Store them in a password manager or print them.
            </p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-canvas rounded-lg border border-line p-4 mb-4" data-testid="backup-codes-list">
              {backupCodes.map((c) => (
                <div key={c} className="text-ink">{c}</div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={copyBackup} className="inline-flex items-center gap-1.5 rounded-full border border-line-strong px-4 py-2 text-xs hover:bg-canvas transition-colors">
                <Copy size={12} weight="thin" /> Copy
              </button>
              <button onClick={downloadBackup} className="inline-flex items-center gap-1.5 rounded-full border border-line-strong px-4 py-2 text-xs hover:bg-canvas transition-colors">
                <DownloadSimple size={12} weight="thin" /> Download .txt
              </button>
              <button
                onClick={() => { setPhase("idle"); setBackupCodes([]); }}
                data-testid="backup-done-btn"
                className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-ink text-canvas px-5 py-2 text-xs hover:bg-ink/90 transition-colors"
              >
                <Check size={12} weight="bold" /> I’ve saved them
              </button>
            </div>
          </motion.div>
        )}

        {enabled && phase === "idle" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2">
            <p className="text-xs text-ink-soft mb-3">
              {remaining !== null ? `${remaining} backup ${remaining === 1 ? "code" : "codes"} remaining.` : ""}
            </p>
            <button
              onClick={() => setPhase("disable")}
              data-testid="disable-2fa-btn"
              className="rounded-full border border-line-strong text-ink px-5 py-2 text-sm hover:bg-canvas transition-colors"
            >
              Turn off two-factor
            </button>
          </motion.div>
        )}

        {phase === "disable" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-2 space-y-4" data-testid="totp-disable-form">
            <p className="text-sm text-ink-soft">Confirm your password and a current 6-digit code (or backup code) to turn off two-factor.</p>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-ink-mute">Password</span>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="disable-2fa-password" className="mt-2 w-full bg-canvas border border-line rounded-lg px-3 py-2 text-sm focus:border-clay focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-ink-mute">Code</span>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} data-testid="disable-2fa-code" className="mt-2 w-full sm:w-48 bg-canvas border border-line rounded-lg px-3 py-2 text-sm font-mono focus:border-clay focus:outline-none" placeholder="123 456 or XXXX-XXXX" />
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={doDisable}
                disabled={loading || !pw || !code}
                data-testid="confirm-disable-2fa"
                className="rounded-full bg-clay text-canvas px-6 py-2 text-sm font-medium hover:bg-clay-dark transition-colors disabled:opacity-40"
              >
                {loading ? "Turning off…" : "Turn off"}
              </button>
              <button onClick={() => { setPhase("idle"); setCode(""); setPw(""); }} className="text-sm text-ink-soft hover:text-ink underline underline-offset-4 decoration-line-strong">
                Never mind
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
